import type { SystemStyleObject } from "@mui/system";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

/** Same order as `AdminCalendar` / dashboard staff lane colours. */
export const CALENDAR_STAFF_PALETTE = ["primary", "secondary", "success", "warning", "info"] as const;
export type CalendarStaffPaletteKey = (typeof CALENDAR_STAFF_PALETTE)[number];

export function staffPaletteKeyFromIndex(staffIdx: number): CalendarStaffPaletteKey {
  return CALENDAR_STAFF_PALETTE[staffIdx % CALENDAR_STAFF_PALETTE.length];
}

/**
 * Shared fill + border for booking chips (calendar day grid + dashboard “today” timeline).
 * Neutral elevated card so chips contrast with grid cells (`action.hover` / paper), not a faint tint wash.
 */
export function calendarBookingEventBaseSx(
  theme: Theme,
  paletteKey: CalendarStaffPaletteKey,
  narrow: boolean,
): SystemStyleObject<Theme> {
  const c = theme.palette[paletteKey];
  const dark = theme.palette.mode === "dark";
  return {
    borderRadius: 1,
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: `3px solid ${c.main}`,
    bgcolor: dark ? theme.palette.grey[800] : theme.palette.common.white,
    px: narrow ? 0.4 : 0.5,
    py: 0.25,
    overflow: "hidden",
    boxSizing: "border-box",
    boxShadow: dark
      ? `0 1px 4px ${alpha(theme.palette.common.black, 0.55)}, 0 0 0 1px ${alpha(theme.palette.common.white, 0.06)}`
      : `0 1px 3px ${alpha(theme.palette.common.black, 0.12)}`,
  };
}

export function calendarBookingTitleSx(theme: Theme, narrow: boolean): SystemStyleObject<Theme> {
  return {
    fontWeight: 600,
    lineHeight: 1.2,
    fontSize: narrow ? "0.65rem" : undefined,
    color: theme.palette.text.primary,
  };
}

export function calendarBookingSubtitleSx(theme: Theme, narrow: boolean): SystemStyleObject<Theme> {
  const dark = theme.palette.mode === "dark";
  return {
    fontSize: narrow ? "0.6rem" : 10,
    lineHeight: 1.15,
    color: dark ? theme.palette.grey[400] : theme.palette.text.secondary,
    opacity: 1,
  };
}
